import { Link, useNavigate } from 'react-router-dom';
import {
    Sparkles,
    Zap,
    ArrowRight,
    CheckCircle,
    Search,
    Share2,
    Megaphone,
    Mail,
    Layout,
    BarChart3,
    Users,
    MessageSquare,
    Rocket,
    Bell,
    Image,
    Wallet,
    ChevronDown,
    Code,
    Briefcase,
    Star,
    Shield,
    Clock,
    CreditCard
} from 'lucide-react';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { modulesApi } from '../../utils/api';
import { useAuthStore } from '../../store/auth';

export default function Automation() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();
    const [productsOpen, setProductsOpen] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
    const [purchasing, setPurchasing] = useState(false);

    // Purchase bundle mutation
    const purchaseMutation = useMutation({
        mutationFn: () => modulesApi.purchaseBundle('bundle_marketer', billingPeriod),
        onSuccess: (response) => {
            // Redirect to DadyPay payment page
            if (response.data?.data?.paymentUrl) {
                window.location.href = response.data.data.paymentUrl;
            }
        },
        onError: (error: any) => {
            console.error('Purchase failed:', error);
            alert(error.response?.data?.message || 'Purchase failed. Please try again.');
            setPurchasing(false);
        }
    });

    const handlePurchase = () => {
        if (!isAuthenticated) {
            // Redirect to register with return URL
            navigate('/register?redirect=/products/marketing-automation&action=buy');
            return;
        }
        setPurchasing(true);
        purchaseMutation.mutate();
    };

    const features = [
        {
            icon: Search,
            title: 'SEO Automation',
            description: 'AI-powered audits, keyword research, backlink analysis, SERP tracking, and content optimization.',
            tools: ['run_seo_audit', 'research_keywords', 'analyze_competitors', 'technical_seo_scan']
        },
        {
            icon: Share2,
            title: 'Social Media',
            description: 'Multi-platform scheduling, AI content generation, hashtag research, and social listening.',
            tools: ['generate_social_post', 'schedule_social_post', 'hashtag_research', 'social_listening']
        },
        {
            icon: Megaphone,
            title: 'Ad Campaigns',
            description: 'Google, Meta, TikTok, LinkedIn ads with AI copy generation and budget optimization.',
            tools: ['create_ad_campaign', 'ad_copy_generate', 'audience_builder', 'budget_optimizer']
        },
        {
            icon: Mail,
            title: 'Email Marketing',
            description: 'AI sequences, A/B testing, deliverability monitoring, and smart personalization.',
            tools: ['generate_email', 'email_sequence_create', 'ab_test_email', 'list_segmentation']
        },
        {
            icon: Layout,
            title: 'Landing Pages',
            description: 'AI page builder, lead capture forms, popups, and conversion optimization.',
            tools: ['landing_page_create', 'form_builder', 'popup_create', 'cta_optimizer']
        },
        {
            icon: BarChart3,
            title: 'Analytics',
            description: 'Funnel analysis, cohort tracking, multi-touch attribution, and predictive insights.',
            tools: ['funnel_analysis', 'cohort_analysis', 'attribution_model', 'predictive_analytics']
        },
        {
            icon: Users,
            title: 'CRM & Pipeline',
            description: 'AI lead scoring, contact management, deal tracking, and pipeline forecasting.',
            tools: ['lead_score', 'contact_create', 'deal_create', 'pipeline_forecast']
        },
        {
            icon: MessageSquare,
            title: 'Chatbot & Support',
            description: 'AI chatbot, live chat, ticket routing, and automated support responses.',
            tools: ['chatbot_train', 'live_chat_setup', 'ticket_create', 'canned_response_generate']
        },
        {
            icon: Rocket,
            title: 'Growth Hacking',
            description: 'Referral programs, viral waitlists, giveaways, and Product Hunt launch prep.',
            tools: ['referral_program_create', 'waitlist_create', 'viral_loop_setup', 'product_hunt_launch']
        },
        {
            icon: Bell,
            title: 'Notifications',
            description: 'Push notifications, SMS campaigns, WhatsApp messaging, and webhook integrations.',
            tools: ['push_notification_send', 'sms_campaign_send', 'whatsapp_send', 'webhook_trigger']
        },
        {
            icon: Image,
            title: 'Creative Studio',
            description: 'AI image generation, video thumbnails, brand kit management, and content repurposing.',
            tools: ['image_generate', 'thumbnail_create', 'brand_kit_apply', 'content_repurpose']
        },
        {
            icon: Wallet,
            title: 'Payments & Billing',
            description: 'Subscription management, invoice generation, churn prediction, and upsell recommendations.',
            tools: ['subscription_create', 'invoice_generate', 'churn_predict', 'upsell_recommend']
        }
    ];

    return (
        <div className="min-h-screen bg-dark-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-950/80 backdrop-blur-lg border-b border-dark-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold">aiOps</span>
                        </Link>
                        <div className="hidden md:flex items-center gap-8">
                            {/* Products Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setProductsOpen(!productsOpen)}
                                    onBlur={() => setTimeout(() => setProductsOpen(false), 150)}
                                    className="flex items-center gap-1 text-white transition"
                                >
                                    Products
                                    <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {productsOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-dark-900 border border-dark-700 rounded-xl shadow-xl overflow-hidden">
                                        <Link
                                            to="/products/marketing-automation"
                                            className="flex items-center gap-3 px-4 py-3 bg-dark-800 transition"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                                                <Zap className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">Marketing Automation</div>
                                                <div className="text-xs text-dark-400">AI-Powered Digital Marketing</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/devai"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                                                <Code className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">DevAI</div>
                                                <div className="text-xs text-dark-400">Developer AI Tools</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/bizops"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
                                                <Briefcase className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">BizOps</div>
                                                <div className="text-xs text-dark-400">Business Operations AI</div>
                                            </div>
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <a href="/#features" className="text-dark-300 hover:text-white transition">Features</a>
                            <a href="/features.html" className="text-dark-300 hover:text-white transition">Docs</a>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/login" className="text-dark-300 hover:text-white transition">
                                Sign in
                            </Link>
                            <Link to="/register" className="btn btn-primary">
                                Start Free Trial
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-orange-600/10 via-transparent to-transparent" />
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl" />

                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 bg-dark-800 border border-dark-700 rounded-full px-4 py-2 mb-8">
                        <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                            <Zap className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm text-dark-300">aiOps Product</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        <span className="bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">
                            Digital Marketing Automation
                        </span>
                    </h1>

                    <p className="text-xl text-dark-300 max-w-3xl mx-auto mb-4">
                        AI-Powered Marketing Automation Platform
                    </p>

                    <p className="text-lg text-dark-400 max-w-2xl mx-auto mb-10">
                        100+ AI tools for SEO, social media, ads, email, landing pages, analytics, CRM, and growth hacking — all automated with intelligent AI.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                        <Link to="/register" className="btn bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg px-8 py-4 flex items-center gap-2">
                            Start Free Trial
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <Link to="/login" className="btn btn-secondary text-lg px-8 py-4">
                            View Demo
                        </Link>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
                        <div className="text-center">
                            <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">100+</div>
                            <div className="text-dark-400 text-sm mt-1">Marketing Tools</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">12</div>
                            <div className="text-dark-400 text-sm mt-1">Modules</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">10x</div>
                            <div className="text-dark-400 text-sm mt-1">Faster Workflows</div>
                        </div>
                        <div className="text-center">
                            <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">AI</div>
                            <div className="text-dark-400 text-sm mt-1">Powered</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 px-4 bg-dark-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            <span className="text-orange-400">12 Modules</span> for Complete Marketing Automation
                        </h2>
                        <p className="text-dark-300 text-lg max-w-2xl mx-auto">
                            Everything you need to automate your marketing stack with AI.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature) => (
                            <div key={feature.title} className="card p-6 hover:border-orange-500/50 transition-all group">
                                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <feature.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                                <p className="text-dark-400 text-sm mb-4">{feature.description}</p>
                                <div className="flex flex-wrap gap-1">
                                    {feature.tools.map((tool) => (
                                        <span key={tool} className="px-2 py-0.5 bg-dark-800 border border-dark-700 rounded text-xs text-dark-400">
                                            {tool}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="py-20 px-4" id="pricing">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            Simple, <span className="text-orange-400">Transparent Pricing</span>
                        </h2>
                        <p className="text-dark-300 text-lg max-w-2xl mx-auto mb-8">
                            Get all 7 marketing modules in one bundle. Save 30% compared to buying separately.
                        </p>

                        {/* Billing Toggle */}
                        <div className="inline-flex items-center gap-4 bg-dark-800 p-1 rounded-xl">
                            <button
                                onClick={() => setBillingPeriod('monthly')}
                                className={`px-4 py-2 rounded-lg transition-all ${billingPeriod === 'monthly'
                                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                                    : 'text-dark-400 hover:text-white'
                                    }`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingPeriod('yearly')}
                                className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${billingPeriod === 'yearly'
                                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                                    : 'text-dark-400 hover:text-white'
                                    }`}
                            >
                                Yearly
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                    Save 20%
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Pricing Card */}
                    <div className="max-w-lg mx-auto">
                        <div className="card p-8 border-2 border-orange-500/50 relative overflow-hidden">
                            {/* Popular Badge */}
                            <div className="absolute top-4 right-4">
                                <span className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full text-sm font-medium">
                                    <Star className="w-4 h-4" /> Most Popular
                                </span>
                            </div>

                            {/* Plan Header */}
                            <div className="mb-6">
                                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-4">
                                    <Rocket className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold">Marketing Suite</h3>
                                <p className="text-dark-400">Complete marketing automation</p>
                            </div>

                            {/* Price */}
                            <div className="mb-6">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-5xl font-bold">
                                        ${billingPeriod === 'yearly' ? '39' : '49'}
                                    </span>
                                    <span className="text-dark-400">/month</span>
                                </div>
                                {billingPeriod === 'yearly' && (
                                    <p className="text-green-400 text-sm mt-1">
                                        $470/year (billed annually) — Save $118!
                                    </p>
                                )}
                                <p className="text-dark-500 text-sm mt-1">
                                    Pay with crypto (BTC, ETH, USDT, USDC)
                                </p>
                            </div>

                            {/* Included Modules */}
                            <div className="mb-6 pb-6 border-b border-dark-700">
                                <h4 className="font-semibold mb-3 flex items-center gap-2">
                                    <span className="text-orange-400">7 Modules</span> Included:
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { name: 'SEO Automation', icon: '🔍' },
                                        { name: 'Social Media', icon: '📱' },
                                        { name: 'Ad Campaigns', icon: '📢' },
                                        { name: 'Email Marketing', icon: '📧' },
                                        { name: 'Analytics', icon: '📊' },
                                        { name: 'Landing Pages', icon: '📄' },
                                        { name: 'CRM & Pipeline', icon: '👥' },
                                    ].map((mod) => (
                                        <div key={mod.name} className="flex items-center gap-2 text-sm">
                                            <span>{mod.icon}</span>
                                            <span className="text-dark-300">{mod.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Features */}
                            <div className="space-y-3 mb-8">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <span className="text-dark-300">100+ AI requests per module daily</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <span className="text-dark-300">Auto-enable modules per project</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <span className="text-dark-300">MCP integration for AI agents</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                                    <span className="text-dark-300">RAG context for personalized AI</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Shield className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                    <span className="text-dark-300">Cancel anytime, no lock-in</span>
                                </div>
                            </div>

                            {/* CTA Button */}
                            <button
                                onClick={handlePurchase}
                                disabled={purchasing || purchaseMutation.isPending}
                                className="w-full btn bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {purchasing || purchaseMutation.isPending ? (
                                    <>
                                        <Clock className="w-5 h-5 animate-spin" />
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-5 h-5" />
                                        Buy Marketing Suite
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>

                            {/* Trust Badges */}
                            <div className="mt-6 flex items-center justify-center gap-4 text-dark-500 text-xs">
                                <span className="flex items-center gap-1">
                                    <Shield className="w-4 h-4" /> Secure Payment
                                </span>
                                <span>•</span>
                                <span>14-Day Free Trial</span>
                                <span>•</span>
                                <span>Instant Access</span>
                            </div>
                        </div>
                    </div>

                    {/* Alternative Options */}
                    <div className="mt-8 text-center">
                        <p className="text-dark-400 mb-4">
                            Need just one module? <Link to="/settings/billing" className="text-orange-400 hover:underline">View individual pricing →</Link>
                        </p>
                        <p className="text-dark-500 text-sm">
                            Or <Link to="/register" className="text-primary-400 hover:underline">start a 14-day free trial</Link> with no credit card required.
                        </p>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="py-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            Perfect for <span className="text-orange-400">Growth Teams</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="card p-6">
                            <h3 className="text-xl font-semibold mb-3">🚀 Startups</h3>
                            <ul className="space-y-2 text-dark-400">
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Launch faster with AI content</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Automate social & email</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Scale without headcount</li>
                            </ul>
                        </div>
                        <div className="card p-6">
                            <h3 className="text-xl font-semibold mb-3">📈 Agencies</h3>
                            <ul className="space-y-2 text-dark-400">
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Manage multiple clients</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> White-label reports</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 10x productivity</li>
                            </ul>
                        </div>
                        <div className="card p-6">
                            <h3 className="text-xl font-semibold mb-3">🏢 Enterprise</h3>
                            <ul className="space-y-2 text-dark-400">
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Full marketing stack</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Advanced analytics</li>
                                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Custom integrations</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4 bg-gradient-to-r from-orange-600/20 to-red-600/20">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-5xl font-bold mb-6">
                        Ready to Automate Your Marketing?
                    </h2>
                    <p className="text-xl text-dark-300 mb-8">
                        Join thousands of marketers using AI to scale faster.
                    </p>
                    <Link to="/register" className="btn bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg px-8 py-4 inline-flex items-center gap-2">
                        Start Free Trial
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4 border-t border-dark-800">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary-400" />
                        <span className="font-semibold">aiOps</span>
                        <span className="text-dark-500">|</span>
                        <span className="text-dark-400">Marketing Automation</span>
                    </div>
                    <div className="text-dark-500 text-sm">
                        © 2026 aiOps. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
}
